import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Box, Button, Typography, Alert, CircularProgress, TextField } from '@mui/material'
import { Email as EmailIcon } from '@mui/icons-material'
import { Auth } from 'aws-amplify'

import { ContentContainer, Divider, SplitHalf, WorkforceSection, WrapperStyled, useAuth, track, eventTypes } from 'torqcloud-ui'
import { appConfig } from '@/config'
import { login } from '@constants/routing'

interface CognitoError extends Error {
  code?: string
}

// Timeout (60s) so users can't spam the resend verification button
const RESEND_VERIFICATION_TIMEOUT = 60000

export default function EmailVerificationPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { authenticationManager, changeLoggedInUser } = useAuth()

  const searchParams = new URLSearchParams(location.search)
  const emailFromQuery = searchParams.get('email')
  const codeFromQuery = searchParams.get('code')

  const email = emailFromQuery || location.state?.email || ''
  const username = location.state?.username || email

  const [code, setCode] = useState(codeFromQuery || '')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resendDisabled, setResendDisabled] = useState(false)

  // Redirect to login if no email provided - we should only enable resend for
  // users who have created an account (naive gate, better than nothing)
  useEffect(() => {
    if (!email) {
      navigate(login())
    }
  }, [email, navigate])

  if (!email) {
    return null
  }

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      setErrorMessage('Please enter the verification code.')
      return
    }

    setVerifying(true)
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      await Auth.confirmSignUp(username, code.trim())
      setSuccessMessage('Email verified successfully!')

      const autoSignInAttempted = Boolean(location.state?.password)
      track(eventTypes.EMAIL_VERIFIED, { email, autoSignInAttempted, fromLogin: Boolean(location.state?.fromLogin) })

      if (location.state?.password) {
        try {
          const user = await Auth.signIn(email, location.state.password)
          changeLoggedInUser(user)
          if (appConfig.enableDomainBasedRegistration) {
            navigate('/join-organization')
          } else {
            navigate('/')
          }
          return
        } catch (signInError) {
          console.error('Error signing in after verification:', signInError)
        }
      }

      // Redirect to login if auto-login wasn't possible or failed
      setTimeout(() => {
        navigate(login(), { state: { email, message: 'Please sign in to continue' } })
      }, 2000)
    } catch (err) {
      const error = err as CognitoError
      console.error('Error verifying code:', error)
      track(eventTypes.EMAIL_VERIFICATION_FAILED, { email, errorCode: error.code })

      // Custom nice messages for users
      // @source https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmSignUp.html#API_ConfirmSignUp_Errors
      if (error.code === 'CodeMismatchException') {
        setErrorMessage('Invalid verification code. Please check and try again.')
      } else if (error.code === 'ExpiredCodeException') {
        setErrorMessage('Verification code has expired. Please request a new code.')
      } else if (error.code === 'NotAuthorizedException' || error.code === 'AliasExistsException') {
        setErrorMessage('Your email is already verified. Redirecting to login...')
        setTimeout(() => {
          navigate(login(), { state: { email } })
        }, 2000)
      } else {
        setErrorMessage('Failed to verify code. Please try again.')
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleResendEmail = async () => {
    setResending(true)
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      if (!authenticationManager.accountManager) {
        throw new Error('Account manager not available')
      }

      await authenticationManager.accountManager.resendVerificationCode(username)

      track(eventTypes.VERIFICATION_CODE_RESENT, { email, success: true })
      setSuccessMessage('Verification code sent! Please check your inbox.')
      setResendDisabled(true)

      setTimeout(() => {
        setResendDisabled(false)
      }, RESEND_VERIFICATION_TIMEOUT)
    } catch (err) {
      const error = err as CognitoError
      console.error('Error resending verification email:', error)
      track(eventTypes.VERIFICATION_CODE_RESENT, { email, success: false })

      // Handle specific error cases
      if (error.code === 'LimitExceededException') {
        setErrorMessage('Too many requests. Please try again in a few minutes.')
      } else if (error.code === 'InvalidParameterException') {
        setErrorMessage('Your email is already verified. Redirecting to login...')
        setTimeout(() => {
          navigate(login(), { state: { email } })
        }, 2000)
      } else {
        setErrorMessage('Failed to resend code. Please try again.')
      }
    } finally {
      setResending(false)
    }
  }

  const handleKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !verifying && code.trim()) {
      await handleVerifyCode()
    }
  }

  return (
    <WrapperStyled>
      <Helmet>
        <title>{`Email Verification | ${appConfig.APP_NAME}`}</title>
      </Helmet>
      <Box style={{ ...ContentContainer }}>
        <WorkforceSection />
        <Divider />
        <Box sx={{ ...SplitHalf, right: 0, margin: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: 3,
            }}
          >
            <Box sx={{ maxWidth: 500, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <EmailIcon sx={{ fontSize: 80, color: 'primary.main' }} />
              </Box>
              <Typography variant="h1" align="center" gutterBottom sx={{ fontSize: '2.25rem', fontWeight: '500', mb: 2 }}>
                Verify Your Email
              </Typography>
              <Typography variant="body1" align="center" color="textPrimary" sx={{ mb: 2 }}>
                We've sent a verification code to
              </Typography>
              <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
                {email}
              </Typography>
              <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 3 }}>
                Please check your email and enter the verification code to complete your registration and join your organization.
              </Typography>
              {successMessage && (
                <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}
              <Box className="form-input" sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Verification Code"
                  name="code"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  size="medium"
                  placeholder="Enter 6-digit code"
                  disabled={verifying}
                  autoFocus={!codeFromQuery}
                  helperText="Check your email for the 6-digit verification code"
                />
              </Box>
              <Box className="max-width" sx={{ mb: 2 }}>
                <Button
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleVerifyCode}
                  disabled={verifying || !code.trim()}
                  variant="contained"
                  startIcon={verifying ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                  {verifying ? 'Verifying...' : 'Verify Email'}
                </Button>
              </Box>
              <Box className="max-width" sx={{ mb: 2 }}>
                <Button
                  color="secondary"
                  fullWidth
                  size="large"
                  onClick={handleResendEmail}
                  disabled={resending || resendDisabled || verifying}
                  variant="outlined"
                  startIcon={resending ? <CircularProgress size={20} color="inherit" /> : undefined}
                >
                  {resending ? 'Sending...' : 'Resend Verification Code'}
                </Button>
              </Box>
              <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
                Didn't receive the code? Check your spam folder or click resend.
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Next Steps:
                </Typography>
                <Box component="ol" sx={{ pl: 2, m: 0 }}>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Check your email inbox for the verification code
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Enter the code in the field above and click "Verify Email"
                  </Typography>
                  <Typography variant="body2" component="li">
                    Once verified, you'll be redirected to sign in
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </WrapperStyled>
  )
}
